import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { ButtonType } from "@/components/lib/Button";
import { Trash2, Plus, Loader2 } from "lucide-react";
import {
  removeItemVariant,
  setItemVariants,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import FormInputField from "@/components/lib/from-input-field";
import CreateCourseItem from "@/components/dialog/menu-manage/common-dialog/create-course-item";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  fetchSingleCreateYourOwnGeneral,
  updateExistingCreateYourOwnGeneral,
} from "@/redux/slice/menu-manage-slice/create-your-own-general-slice";

function Step2({ form, item_id }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);

  useEffect(() => {
    if (!item_id) return;
    const fetchSingleItem = async () => {
      try {
        const res = await dispatch(fetchSingleCreateYourOwnGeneral(item_id));
        if (fetchSingleCreateYourOwnGeneral.fulfilled.match(res)) {
          const item = res?.payload?.courses;
          if (item) {
            dispatch(setItemVariants(item || []));
          }
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchSingleItem();
  }, [dispatch, item_id]);

  const handleItemUpdate = async (payload, actionType = "add") => {
    if (!payload) return;
    setLoading(true);
    let allItem = [...itemVariants];
    if (actionType === "add") {
      allItem = [...itemVariants, payload];
    } else if (actionType === "remove") {
      allItem = itemVariants.filter((item) => item.unique_id !== payload);
    }
    form.setValue("courses", allItem);

    if (item_id) {
      try {
        await dispatch(
          updateExistingCreateYourOwnGeneral({
            id: item_id,
            data: {
              name: form.watch("name"),
              status: `${
                form.watch("type") === "update" ? form.watch("status") : "draft"
              }`,
              courses: allItem,
            },
          })
        );
      } catch (error) {
        toast.error("Failed to update courses.");
        setLoading(false);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    form.setValue("courses", itemVariants);
  }, [itemVariants, form]);

  const handleRemove = async (id) => {
    const updatedItemVariants = itemVariants.filter(
      (course) => course.unique_id !== id
    );
    dispatch(setItemVariants(updatedItemVariants));
    form.setValue("courses", updatedItemVariants);
    await handleItemUpdate(id, "remove");
    toast.success("Course removed successfully");
  };

  const handleRemoveItem = (itemId, itemType) => {
    const updated = itemVariants.map((course) => ({
      ...course,
      course_items: course?.course_items?.filter(
        (item) => !(item.item_id === itemId && item.item_type === itemType)
      ),
    }));

    const allEmpty = updated.every(
      (course) => course.course_items?.length === 0
    );

    dispatch(setItemVariants(allEmpty ? [] : updated));
    form.setValue("courses", allEmpty ? [] : updated);
    toast.success("Course Item removed successfully");
  };

  return (
    <Card className="max-w-screen-md bg-cblack6 mx-auto mt-10 text-cwhite">
      <CardHeader>
        <CardTitle>
          {form.watch("type") === "update"
            ? "Update Choose Your Own"
            : "Add New Choose Your Own"}
        </CardTitle>
        <CardDescription className="text-cyellow">
          Item name: {form.watch("name") || "No name"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Items Section */}
        <div className="flex justify-between items-center mb-4">
          <h2>Course List</h2>
          <ButtonType
            onClick={() => setOpenDialog(true)}
            className="bg-cgreen/70 text-cwhite flex flex-shrink-0"
          >
            <span className="flex items-center">
              <Plus className="w-4 h-4 mr-1" size={18} /> Add New Course
            </span>
          </ButtonType>
        </div>

        <div className="flex flex-col gap-5 relative">
          {itemVariants &&
            itemVariants?.length > 0 &&
            itemVariants?.map((variant, val) => (
              <div className="border border-gray-500 rounded" key={val}>
                <div className="flex justify-between items-center gap-5 py-3 bg-cblack5 px-3">
                  <div className="flex items-end gap-2">
                    <h2 className="text-lg tracking-tight font-semibold">
                      {variant?.name}
                    </h2>
                    <p className="text-sm text-gray-400">{variant?.description}</p>
                  </div>
                  <Trash2
                    onClick={() => handleRemove(variant?.unique_id)}
                    className="text-cred cursor-pointer"
                    size={18}
                  />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {variant?.course_items &&
                    variant?.course_items?.length > 0 ? (
                      variant?.course_items?.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            {item?.name ||
                              item?.display_name ||
                              item?.menu_item?.name ||
                              "Data not available"}
                            {(item?.variant_name || item?.variant?.name) &&
                              ` (${item?.variant_name || item?.variant?.name})`}
                          </TableCell>
                          <TableCell>{item.price ?? "0"}</TableCell>

                          <TableCell className="text-right">
                            <span className="flex items-center justify-end gap-3">
                              <Trash2
                                onClick={() =>
                                  handleRemoveItem(
                                    item?.item_id,
                                    item?.item_type
                                  )
                                }
                                className="w-4 h-4 text-cred cursor-pointer"
                              />
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center">
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            ))}
          {loading && (
            <div className="absolute inset-0 bg-black/40 flex justify-center items-center z-10">
              <Loader2 className="animate-spin" size={18} />
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <FormInputField
            name="person_limit"
            style="bg-cblack7"
            placeholder={"1"}
            label={"Person limit"}
            form={form}
            type="text"
          />
        </div>
      </CardContent>

      {/* Dialog */}
      <CreateCourseItem
        title={"Create New Course"}
        isOpen={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={(newItem) => handleItemUpdate(newItem, "add")}
      />
    </Card>
  );
}

export default Step2;

